import { icons as LucideIcons } from 'lucide-react';

import { AtlasIcons } from '@crossnokaye/ux-animatics';
import { Icon, withTooltip } from '@crossnokaye/ux-common';

import { ActionIconVariant as ActionIconVariantEnum } from '@repo/sdk/models/components';

type IconVariant = {
  color: string;
  backgroundColor?: string;
};

const ActionIconVariants: Record<string, IconVariant> = {
  [ActionIconVariantEnum.Default]: {
    color: 'stroke-foreground',
    backgroundColor: 'bg-input',
  },
  [ActionIconVariantEnum.EStop]: {
    color: 'stroke-foreground fill-foreground',
    backgroundColor: 'bg-input',
  },
  [ActionIconVariantEnum.On]: {
    color: 'stroke-primary-foreground',
    backgroundColor: 'bg-primary',
  },
  [ActionIconVariantEnum.Satisfied]: {
    color: 'stroke-primary-foreground',
    backgroundColor: 'bg-primary',
  },
  [ActionIconVariantEnum.Off]: {
    color: 'stroke-secondary-foreground',
    backgroundColor: 'bg-secondary',
  },
  [ActionIconVariantEnum.Critical]: {
    color: 'text-foreground',
    backgroundColor: 'bg-ckds-base-critical',
  },
  [ActionIconVariantEnum.Faulted]: {
    color: 'text-foreground',
    backgroundColor: 'bg-ckds-base-critical',
  },
  [ActionIconVariantEnum.Warning]: {
    color: 'text-background',
    backgroundColor: 'bg-ckds-base-warning',
  },
  [ActionIconVariantEnum.Caution]: {
    color: 'text-background',
    backgroundColor: 'bg-ckds-base-caution',
  },
  [ActionIconVariantEnum.Eai]: {
    color: 'stroke-none fill-white',
    backgroundColor: 'bg-conserving',
  },
  [ActionIconVariantEnum.ModeCaution]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-base-caution',
  },
  [ActionIconVariantEnum.ModeConserving]: {
    color: 'stroke-none fill-white',
    backgroundColor: 'bg-conserving',
  },
  [ActionIconVariantEnum.ModeCooling]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-cooling',
  },
  [ActionIconVariantEnum.ModeDefault]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-neutral-50',
  },
  [ActionIconVariantEnum.ModeSecondary]: {
    color: 'stroke-ckds-neutral-200',
    backgroundColor: 'bg-ckds-neutral-700',
  },
  [ActionIconVariantEnum.ModeTertiary]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-neutral-200',
  },
  [ActionIconVariantEnum.ModeWarning]: {
    color: 'stroke-ckds-neutral-900',
    backgroundColor: 'bg-ckds-base-warning',
  },
  [ActionIconVariantEnum.Oos]: {
    color: 'stroke-foreground',
    backgroundColor: 'bg-ckds-neutral-500',
  },
} as const;

type ActionIconVariant = keyof typeof ActionIconVariants;

interface ActionIconProps {
  className?: string;
  fallbackIcon?: string;
  icon: string;
  isPulsing?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  tooltipContent?: string;
  variant?: ActionIconVariant;
}

const IconWithTooltip = withTooltip(Icon);

/**
 * Returns the action icon component
 * @param className - The class name to apply to the icon
 * @param fallbackIcon - The fallback icon to use if the icon is not found
 * @param icon - The icon to use for the action
 * @param isPulsing - Whether the icon is pulsing
 * @param size - The size of the icon
 * @param tooltipContent - The tooltip content to apply to the icon, if any
 * @param variant - The variant of the icon
 * @returns The action icon component with tooltip if tooltipContent is provided
 */
export const ActionIcon = ({
  className,
  fallbackIcon,
  icon,
  isPulsing,
  size = 'md',
  tooltipContent,
  variant = 'default',
}: ActionIconProps) => {
  const { color, backgroundColor } = ActionIconVariants[variant];

  let IconComponent =
    AtlasIcons[icon as keyof typeof AtlasIcons] || LucideIcons[icon as keyof typeof LucideIcons];

  if (!IconComponent && fallbackIcon) {
    IconComponent = LucideIcons[fallbackIcon as keyof typeof LucideIcons];
  }

  if (!IconComponent) {
    return null;
  }

  if (tooltipContent) {
    return (
      <IconWithTooltip
        backgroundColor={backgroundColor}
        className={className}
        color={color}
        delayDuration={100}
        icon={IconComponent}
        isPulsing={isPulsing}
        size={size}
        tooltipContent={tooltipContent}
        contentClassName="whitespace-pre-line"
        variant={'rounded'}
      />
    );
  }
  return (
    <Icon
      backgroundColor={backgroundColor}
      className={className}
      color={color}
      icon={IconComponent}
      isPulsing={isPulsing}
      size={size}
      variant={'rounded'}
    />
  );
};
